import { defineStore } from 'pinia'
import { ref } from 'vue'

// ============================================================
// 全局确认弹窗 Store（Promise 化）
//
// 用途：让「非组件上下文」（如工具的 execute()、composable、store action）
// 也能弹出 MeowChat 风格的确认框，并 await 用户的选择。
//
// 用法：
//   const ok = await useGlobalConfirmStore().confirm({
//     title: '删除 MCP 服务器',
//     message: '删除后配置将一并移除，确认？',
//     confirmText: '删除',
//     confirmVariant: 'danger',
//   })
//   if (!ok) return
//
// 需要在应用根部挂一次 <GlobalConfirmDialog />（见 App.vue）来渲染弹窗。
// ============================================================

export interface GlobalConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'danger' | 'primary'
}

export const useGlobalConfirmStore = defineStore('global-confirm', () => {
  const show = ref(false)
  const title = ref('')
  const message = ref('')
  const confirmText = ref('确认')
  const cancelText = ref('取消')
  const confirmVariant = ref<'danger' | 'primary'>('primary')

  // 当前挂起的 resolver：用户点击确认/取消时调用它兑现 Promise
  let resolver: ((value: boolean) => void) | null = null

  /** 发起一次确认，返回用户是否确认的 Promise */
  function confirm(options: GlobalConfirmOptions): Promise<boolean> {
    // 若已有一个弹窗挂起（极端并发），先把旧的当作取消兑现，避免 resolver 丢失
    if (resolver) {
      resolver(false)
      resolver = null
    }

    title.value = options.title
    message.value = options.message
    confirmText.value = options.confirmText ?? '确认'
    cancelText.value = options.cancelText ?? '取消'
    confirmVariant.value = options.confirmVariant ?? 'primary'
    show.value = true

    return new Promise<boolean>((resolve) => {
      resolver = resolve
    })
  }

  /** 宿主组件在用户点击「确认」时调用 */
  function handleConfirm(): void {
    show.value = false
    resolver?.(true)
    resolver = null
  }

  /** 宿主组件在用户点击「取消」/关闭时调用 */
  function handleCancel(): void {
    show.value = false
    resolver?.(false)
    resolver = null
  }

  return {
    show,
    title,
    message,
    confirmText,
    cancelText,
    confirmVariant,
    confirm,
    handleConfirm,
    handleCancel,
  }
})
